import { PlusCircle } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import { Tag } from "common/components/ui/Tag";

import LabelsDropdownMenu from "../LabelsDropdownMenu";
import LabelTag from "../LabelTag";

import type { Label } from "../../types";

const alignClassNames = {
  center: "justify-center",
  end: "justify-end",
  start: "justify-start",
} as const;

interface LabelsProps {
  labels: Label[];
  onRemoveLabel: (label: Label) => void;
  onAddLabel: (label: Label) => Promise<void>;
  align: "center" | "end" | "start";
  maxLabels?: number;
  size?: "sm" | "md";
}

export default function Labels({
  labels,
  onRemoveLabel,
  onAddLabel,
  align,
  size = "md",
  maxLabels = Infinity,
}: LabelsProps) {
  const [isOpen, setIsOpen] = useState(false);

  function toggleLabel(label: Label, isSelected: boolean) {
    (isSelected ? onRemoveLabel : onAddLabel)(label);
  }

  const addLabelComponent = (
    <Tag
      variant="tertiary"
      size={size}
      leftIcon={<PlusCircle weight="fill" />}
      asChild
    >
      <button type="button" onClick={() => setIsOpen(true)}>
        Add new
      </button>
    </Tag>
  );

  return (
    <LabelsDropdownMenu
      labels={labels}
      toggleLabel={toggleLabel}
      onAddLabel={onAddLabel}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div
        className={clsx(
          "flex overflow-hidden flex-wrap items-center gap-1.5",
          alignClassNames[align],
        )}
      >
        {labels.length ? (
          <>
            {labels.slice(0, maxLabels).map((label) => (
              <LabelTag
                key={label.id}
                label={label}
                size={size}
                onClick={() => setIsOpen(true)}
              />
            ))}
            {labels.length > maxLabels && (
              <Tag variant="tertiary" size={size} asChild>
                <button type="button">+{labels.length - maxLabels}</button>
              </Tag>
            )}
            {addLabelComponent}
          </>
        ) : (
          addLabelComponent
        )}
      </div>
    </LabelsDropdownMenu>
  );
}
